import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { Button } from 'bootstrap-4-react/lib/components';
import { getPendingSignupNotes } from '../../store/actions';
import { formatNoteDate } from './statusUtils';

// Beyond this many notes the list scrolls inside the popup.
const NOTES_BEFORE_SCROLL = 4;

// Read-only popup listing every note of a user request, newest first.
const NotesSummaryModal = ({ show, item, onClose }) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!show || !item?.Id) return undefined;
        let cancelled = false;
        setLoading(true);
        setError('');
        getPendingSignupNotes(item.Id)
            .then((res) => {
                if (cancelled) return;
                if (res?.data?.Status === 200) {
                    setNotes(res.data.EntityList || []);
                } else {
                    setNotes([]);
                    setError(res?.data?.Message?.AppStatusDescription || 'Failed to load notes');
                }
            })
            .catch(() => {
                if (cancelled) return;
                setNotes([]);
                setError('Failed to load notes');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [show, item]);

    return (
        <Dialog
            open={!!show}
            onClose={onClose}
            aria-labelledby="notes-summary-title"
            fullWidth
            maxWidth="sm"
            scroll="paper"
        >
            <DialogTitle id="notes-summary-title">
                <i className="fa fa-sticky-note-o mr-2" aria-hidden="true"></i>Notes Summary
            </DialogTitle>
            <DialogContent dividers>
                {item && (
                    <div className="mb-3">
                        <strong>{item.FullName}</strong>
                        {item.Email ? <span className="text-muted"> · {item.Email}</span> : null}
                    </div>
                )}
                {loading && <div className="text-muted">Loading notes...</div>}
                {!loading && error && <div className="text-danger">{error}</div>}
                {!loading && !error && notes.length === 0 && <div className="text-muted">No notes added yet.</div>}
                {!loading && notes.length > 0 && (
                    <div
                        data-testid="notes-list"
                        style={notes.length > NOTES_BEFORE_SCROLL ? { maxHeight: 360, overflowY: 'auto', paddingRight: 8 } : undefined}
                    >
                        {notes.map((note) => (
                            <div key={note.Id} className="mb-3 pb-2 border-bottom" data-testid="note-entry">
                                <div className="text-muted small">
                                    {note.CreatedByName || 'Unknown user'} · {formatNoteDate(note.CreatedDate)}
                                </div>
                                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{note.NoteText}</div>
                            </div>
                        ))}
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button className="cus-primary-transparent-btn" onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

NotesSummaryModal.propTypes = {
    show: PropTypes.bool,
    item: PropTypes.shape({
        Id: PropTypes.number,
        FullName: PropTypes.string,
        Email: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
};

NotesSummaryModal.defaultProps = {
    show: false,
    item: null,
};

export default NotesSummaryModal;
